import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SetupStatus, User } from '@couch/shared';
import { createContext, useContext, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ApiError, api, errorMessage } from '../api/client';

interface Session {
  user: User | null;
  setup: SetupStatus;
  /** Store the user the server just signed in (login, setup, invite). */
  signedIn: (user: User) => void;
  signOut: () => Promise<void>;
  /** Forget the user here when the server already ended the session (a deleted account, doc 66); the note shows on the sign-in page. */
  signedOut: (note?: string) => void;
}

const SessionContext = createContext<Session | null>(null);

export const ME_KEY = ['me'] as const;
export const SETUP_KEY = ['setup'] as const;
export const AUTH_NOTE_KEY = ['auth-note'] as const;

async function currentUser(): Promise<User | null> {
  try {
    return (await api.me()).user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const setup = useQuery({ queryKey: SETUP_KEY, queryFn: api.setupStatus });
  const me = useQuery({ queryKey: ME_KEY, queryFn: currentUser });

  if (setup.isPending || me.isPending) return <p className="page-status" role="status">Loading…</p>;
  if (setup.isError || me.isError) {
    return <p className="page-status" role="alert">{errorMessage(setup.error ?? me.error)}</p>;
  }

  // Not queryClient.clear(): it detaches the observers of `me` and `setup` here, so
  // the null below would never re-render this provider and the UI stays signed in.
  const forget = () => {
    queryClient.setQueryData(ME_KEY, null);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] && q.queryKey[0] !== SETUP_KEY[0] });
  };
  const session: Session = {
    user: me.data,
    setup: setup.data,
    signedIn: (user) => {
      queryClient.setQueryData(ME_KEY, user);
      void queryClient.invalidateQueries({ queryKey: SETUP_KEY });
    },
    signOut: async () => {
      await api.logout();
      forget();
    },
    signedOut: (note) => {
      forget();
      if (note) queryClient.setQueryData(AUTH_NOTE_KEY, note);
    },
  };
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession outside SessionProvider');
  return session;
}

/** The signed-in user; only call below RequireUser. */
export function useUser(): User {
  const { user } = useSession();
  if (!user) throw new Error('useUser without a signed-in user');
  return user;
}

export function RequireUser({ children }: { children: ReactNode }) {
  const { user, setup } = useSession();
  const location = useLocation();
  if (setup.setupEnabled) return <Navigate to="/setup" replace />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

export function RequireOwner({ children }: { children: ReactNode }) {
  const user = useUser();
  if (user.role !== 'owner') return <p className="notice">Only the owner can see this page.</p>;
  return children;
}

/** Login and setup are for signed-out visitors only. */
export function PublicOnly({ children, page }: { children: ReactNode; page: 'login' | 'setup' | 'signup' }) {
  const { user, setup } = useSession();
  const location = useLocation();
  // Back to where sign-in was asked for (a deep link, an app's OAuth request), else home.
  if (user) return <Navigate to={(location.state as { from?: string } | null)?.from ?? '/'} replace />;
  if (page !== 'setup' && setup.setupEnabled) return <Navigate to="/setup" replace />;
  if (page === 'setup' && !setup.setupEnabled) return <Navigate to="/login" replace />;
  return children;
}
